import { atom } from 'infrastructure/state/jotai.wrapper'
import { Single } from 'infrastructure/api'
import { doAdminQueries } from 'infrastructure/api'
import { PlatformFeatures } from 'shared/api/resources'
import { loadable } from 'jotai/utils'
import {
  findFullOrPartialMatchingPackages,
  findMissingOrIncompletePackages,
} from './platformFeatures.utils'
import { districtResponseAtom } from 'shared/api/district'
import { schoolResponseAtom } from 'shared/api/school'

type ActionType = 'add' | 'remove' | ''
type EntityType = 'school' | 'district'

type SelectedAction = {
  actionType: ActionType
  entityType: EntityType
  locked?: boolean
}

// When this value is set we see the confirmation dialog
// Closing the dialog sets this value to undefined
// actionType defines adding or removing feature
// entity type defines adding to school or district
export const selectedActionAtom = atom<SelectedAction | undefined>(undefined)

export const selectedFeaturePackageAtom = atom('')

async function fetchPlatformFeatures() {
  return doAdminQueries<Single.PlatformFeature[]>([
    {
      resourceData: PlatformFeatures,
      params: {},
    },
  ])
}

const platformFeaturesAtom = loadable(
  atom(async () => {
    const features = await fetchPlatformFeatures()
    return features.data.platform_features
  }),
)

export const platformFeaturesWithDefaultAtom = atom((get) => {
  const features = get(platformFeaturesAtom)
  if (features.state === 'hasData') {
    return features.data
  }
  return []
})

type SchoolWithFeatures = {
  id: string | number
  name: string
  features: { name: string }[]
}
export const selectedSchoolsAtom = atom<SchoolWithFeatures[]>([])

export const canAddPackageToSchoolsAtom = atom((get) => {
  const selectedSchools = get(selectedSchoolsAtom)
  const selectedPackageName = get(selectedFeaturePackageAtom)

  if (!selectedSchools.length || !selectedPackageName) return false

  const allAvailablePackages = selectedSchools.reduce<string[]>(
    (prev, school) => {
      return [...prev, ...findMissingOrIncompletePackages(school.features)]
    },
    [],
  )
  return allAvailablePackages.includes(selectedPackageName)
})

export const canRemovePackageFromSchoolsAtom = atom((get) => {
  const selectedSchools = get(selectedSchoolsAtom)
  const selectedPackageName = get(selectedFeaturePackageAtom)

  if (!selectedSchools.length || !selectedPackageName) return false

  const allMatchedPackages = selectedSchools.reduce<string[]>(
    (prev, school) => {
      return [...prev, ...findFullOrPartialMatchingPackages(school?.features)]
    },
    [],
  )
  return allMatchedPackages.includes(selectedPackageName)
})

export const availableDistrictPackagesAtom = atom(async (get) => {
  const district = await get(districtResponseAtom)
  if (!district) return []
  return findMissingOrIncompletePackages(district.features)
})

export const availableSchoolPackagesAtom = atom(async (get) => {
  const school = await get(schoolResponseAtom)
  if (!school) return []
  return findMissingOrIncompletePackages(school.features)
})
